import React, { useState, useEffect } from 'react';
import { FiX, FiCopy, FiCheck, FiDownload, FiRefreshCw, FiAlertCircle } from 'react-icons/fi';
import { getAiCache, saveAiCache, type AiCacheContentType, type AiCacheHistoryEntry } from '../../api/ai';
import AiCacheActions from './AiCacheActions';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon: React.ReactNode;
  iconBg: string;
  fetchContent: () => Promise<{ content: string; metadata: { model?: string; tokensUsed: number; durationMs: number } }>;
  filename: string;
  architectureId?: string;
  cacheType?: AiCacheContentType;
}

function markdownToHtml(md: string): string {
  const fencedBlocks: string[] = [];
  let processed = md.replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) => {
    const idx = fencedBlocks.length;
    fencedBlocks.push(`<pre class="code-block"><code class="lang-${lang || 'text'}">${code.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</code></pre>`);
    return `\x00BLOCK${idx}\x00`;
  });

  processed = processed
    .replace(/^#{4}\s+(.+)$/gm, '<h4>$1</h4>')
    .replace(/^#{3}\s+(.+)$/gm, '<h3>$1</h3>')
    .replace(/^#{2}\s+(.+)$/gm, '<h2>$1</h2>')
    .replace(/^#{1}\s+(.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/^---$/gm, '<hr/>')
    .replace(/^\|(.+)\|$/gm, (row) => {
      const cells = row.split('|').filter((_, i, a) => i > 0 && i < a.length - 1);
      return '<tr>' + cells.map(c => c.trim().match(/^[-:]+$/) ? '' : `<td>${c.trim()}</td>`).join('') + '</tr>';
    })
    .replace(/(<tr>.*<\/tr>\n?)+/g, m => `<table>${m}</table>`)
    .replace(/^>\s+(.+)$/gm, '<blockquote>$1</blockquote>')
    .replace(/^[-*]\s+(.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, m => `<ul>${m}</ul>`)
    .replace(/\n\n+/g, '</p><p>')
    .replace(/\n/g, '<br/>');

  processed = processed.replace(/\x00BLOCK(\d+)\x00/g, (_, i) => fencedBlocks[parseInt(i)]);
  return `<p>${processed}</p>`;
}

const MarkdownViewerModal: React.FC<Props> = ({ isOpen, onClose, title, icon, iconBg, fetchContent, filename, architectureId, cacheType }) => {
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [content, setContent]   = useState('');
  const [metadata, setMetadata] = useState<{ model?: string; tokensUsed: number; durationMs: number } | null>(null);
  const [viewMode, setViewMode] = useState<'preview' | 'raw'>('preview');
  const [copied, setCopied]     = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const [cacheUpdatedAt, setCacheUpdatedAt] = useState<string | undefined>();

  useEffect(() => {
    if (!isOpen) return;
    setContent(''); setError(null); setViewMode('preview'); setFromCache(false);
    setCacheUpdatedAt(undefined);
    loadOrGenerate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadOrGenerate = async () => {
    if (architectureId && cacheType) {
      try {
        const cached = await getAiCache(architectureId, cacheType);
        setContent(cached.content.text);
        setMetadata(cached.content.metadata || null);
        setFromCache(true);
        setCacheUpdatedAt(cached.updatedAt);
        return;
      } catch {
        // no cache, generate fresh
      }
    }
    generate();
  };

  const generate = async () => {
    setLoading(true); setError(null); setFromCache(false); setCacheUpdatedAt(undefined);
    try {
      const result = await fetchContent();
      setContent(result.content);
      setMetadata(result.metadata);
      if (architectureId && cacheType) {
        saveAiCache(architectureId, cacheType, { text: result.content, metadata: result.metadata }).catch(() => {});
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (entry: AiCacheHistoryEntry) => {
    setContent(entry.content.text);
    setMetadata(entry.content.metadata || null);
    setFromCache(true);
    setCacheUpdatedAt(entry.savedAt);
  };

  const handleExportPdf = () => {
    if (!content) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${title}</title>
    <style>body{font-family:system-ui,sans-serif;padding:32px;max-width:860px;margin:0 auto;color:#1f2937;line-height:1.7;font-size:14px}
    h1{font-size:22px;font-weight:700;color:#111827;margin:24px 0 12px;border-bottom:2px solid #e5e7eb;padding-bottom:8px}
    h2{font-size:18px;font-weight:600;color:#1f2937;margin:20px 0 10px}h3{font-size:15px;font-weight:600;margin:16px 0 8px}
    p{margin:8px 0}ul{margin:8px 0;padding-left:20px}li{margin:4px 0}
    table{border-collapse:collapse;width:100%;margin:12px 0}td,th{border:1px solid #e5e7eb;padding:8px 12px;font-size:13px}th{background:#f9fafb;font-weight:600}
    code{background:#f3f4f6;padding:2px 6px;border-radius:4px;font-family:monospace;font-size:13px}
    pre{background:#1e1e2e;color:#cdd6f4;padding:16px;border-radius:8px;overflow-x:auto;margin:12px 0;font-size:13px}
    blockquote{border-left:4px solid #e5e7eb;margin:12px 0;padding:8px 16px;background:#f9fafb}
    @media print{body{padding:16px}}</style></head><body>${markdownToHtml(content)}</body></html>`);
    w.document.close();
    w.print();
  };

  const copy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const download = () => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">

        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center`}>{icon}</div>
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          </div>
          <div className="flex items-center gap-2">
            {content && (
              <>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
                  <button onClick={() => setViewMode('preview')} className={`px-3 py-1.5 ${viewMode === 'preview' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`}>Aperçu</button>
                  <button onClick={() => setViewMode('raw')} className={`px-3 py-1.5 ${viewMode === 'raw' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`}>Markdown</button>
                </div>
                <button onClick={copy} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
                  {copied ? <FiCheck size={12} className="text-green-500" /> : <FiCopy size={12} />}
                  {copied ? 'Copié' : 'Copier'}
                </button>
                <button onClick={download} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
                  <FiDownload size={12} /> .md
                </button>
              </>
            )}
            <button onClick={generate} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
              <FiRefreshCw size={12} /> Regénérer
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"><FiX size={18} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-gray-600 rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Génération en cours…</p>
            </div>
          )}

          {error && (
            <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <FiAlertCircle className="text-red-500 shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm font-medium text-red-700">Erreur</p>
                <p className="text-xs text-red-600 mt-1">{error}</p>
                <button onClick={generate} className="mt-2 text-xs text-red-700 underline">Réessayer</button>
              </div>
            </div>
          )}

          {content && (
            viewMode === 'raw'
              ? <pre className="p-6 text-xs font-mono text-gray-700 whitespace-pre-wrap leading-relaxed">{content}</pre>
              : (
                <iframe
                  srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"/><style>
                    body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;padding:32px;max-width:860px;margin:0 auto;color:#1f2937;line-height:1.7;font-size:14px}
                    h1{font-size:22px;font-weight:700;color:#111827;margin:24px 0 12px;border-bottom:2px solid #e5e7eb;padding-bottom:8px}
                    h2{font-size:18px;font-weight:600;color:#1f2937;margin:20px 0 10px}
                    h3{font-size:15px;font-weight:600;color:#374151;margin:16px 0 8px}
                    h4{font-size:13px;font-weight:600;color:#4b5563;margin:12px 0 6px}
                    p{margin:8px 0}ul{margin:8px 0;padding-left:20px}li{margin:4px 0}
                    table{border-collapse:collapse;width:100%;margin:12px 0}td,th{border:1px solid #e5e7eb;padding:8px 12px;text-align:left;font-size:13px}
                    th{background:#f9fafb;font-weight:600}tr:nth-child(even){background:#f9fafb}
                    code{background:#f3f4f6;padding:2px 6px;border-radius:4px;font-family:monospace;font-size:13px}
                    .code-block{background:#1e1e2e;color:#cdd6f4;padding:16px;border-radius:8px;overflow-x:auto;margin:12px 0}
                    .code-block code{background:none;color:inherit;padding:0;font-size:13px}
                    blockquote{border-left:4px solid #e5e7eb;margin:12px 0;padding:8px 16px;background:#f9fafb;color:#4b5563;border-radius:0 8px 8px 0}
                    hr{border:none;border-top:1px solid #e5e7eb;margin:20px 0}
                    strong{font-weight:600}
                  </style></head><body>${markdownToHtml(content)}</body></html>`}
                  className="w-full border-0"
                  style={{ height: '100%', minHeight: '500px' }}
                  sandbox="allow-same-origin"
                  title="preview"
                />
              )
          )}
        </div>

        {(metadata || content) && (
          <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400 shrink-0">
            <AiCacheActions
              architectureId={architectureId}
              cacheType={cacheType}
              content={{ text: content, metadata }}
              fromCache={fromCache}
              updatedAt={cacheUpdatedAt}
              onRestore={handleRestore}
              onExportPdf={handleExportPdf}
            />
            <div className="flex items-center gap-4">
              {metadata?.model && <span className="font-medium text-gray-500">{metadata.model}</span>}
              {metadata && <><span>{metadata.tokensUsed.toLocaleString()} tokens</span><span>{(metadata.durationMs / 1000).toFixed(1)}s</span></>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarkdownViewerModal;
